import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChartLineUp,
  ArrowSquareOut,
  TrendUp,
  ShieldCheck,
  Target,
  Brain,
  Lock,
  Warning,
  CaretDown,
  CaretUp,
  Star,
  CalendarBlank,
  Coffee,
  Flag,
  Triangle,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tradingViewStockUrl, screenerStockUrl } from "../../lib/tradingview";
import { useAuth } from "../../contexts/AuthContext";
import AdminEditTradePlan from "../AdminEditTradePlan";
import StockList from "./StockList";

const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

const ratingColor = {
  Strong: "text-pos border-pos bg-pos-soft",
  Moderate: "text-mod border-mod bg-mod-soft",
  Weak: "text-neg border-neg bg-neg-soft",
};

const scoreColor = (s) => {
  if (s == null) return "text-muted-fg border-subtle";
  if (s >= 8) return "text-pos border-pos bg-pos-soft";
  if (s >= 6) return "text-mod border-mod bg-mod-soft";
  return "text-neg border-neg bg-neg-soft";
};

// Pattern-specific theming: left-border accent + chip color + icon.
// Used for the kind chip and the card's left edge accent stripe.
const PATTERN_THEME = {
  short:        { accent: "hsl(var(--signal-moderate))",  chipClass: "text-mod border-mod bg-mod-soft", icon: "Coffee" },
  long:         { accent: "hsl(var(--signal-positive))",  chipClass: "text-pos border-pos bg-pos-soft", icon: "Coffee" },
  flag:         { accent: "hsl(var(--signal-positive))",  chipClass: "text-pos border-pos/60 bg-pos-soft", icon: "Flag" },
  asc_triangle: { accent: "hsl(var(--signal-moderate))",  chipClass: "text-mod border-mod/60 bg-mod-soft", icon: "Triangle" },
};
const KIND_LABEL = {
  short: { label: "Short Cup", desc: "Weekly close confirmation · SL = weekly low" },
  long: { label: "Long Cup", desc: "Monthly close confirmation · SL = monthly low" },
  flag: { label: "Bull Flag", desc: "Flagpole + tight consolidation · SL = flag low" },
  asc_triangle: { label: "Asc. Triangle", desc: "Flat resistance + rising lows · SL = rising low" },
};

function CupCard({ item, onWatch, watched, onOpenStock, onItemUpdated }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const sym = (item.symbol || "").replace(".NS", "");
  const kindInfo = KIND_LABEL[item.kind] || KIND_LABEL.short;
  const theme = PATTERN_THEME[item.kind] || PATTERN_THEME.short;
  const KindIcon =
    theme.icon === "Flag" ? Flag :
    theme.icon === "Triangle" ? Triangle :
    Coffee;
  const distFromEntry = item.entry
    ? ((item.last_price - item.entry) / item.entry) * 100
    : 0;

  // Open the StockDetailModal with cup overlay when the user clicks the
  // card body. Buttons inside (TV / SCR / Watch / Fundamentals toggle)
  // call `e.stopPropagation()` themselves so the card click only fires on
  // the empty surface.
  const openDetail = () => {
    if (!onOpenStock) return;
    onOpenStock({
      ...item,
      cup_mode: true,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={openDetail}
      role={onOpenStock ? "button" : undefined}
      tabIndex={onOpenStock ? 0 : undefined}
      onKeyDown={(e) => {
        if (onOpenStock && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openDetail();
        }
      }}
      className={
        "border border-subtle bg-surface rounded-sm p-5 hover:border-pos/40 transition relative overflow-hidden " +
        (onOpenStock ? "cursor-pointer" : "")
      }
      style={{ borderLeftWidth: 3, borderLeftColor: theme.accent }}
      data-testid={`cup-card-${sym}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <a
              href={tradingViewStockUrl(item.symbol)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="font-mono text-lg font-bold text-primary-fg hover:text-pos flex items-center gap-1"
              data-testid={`cup-tv-${sym}`}
            >
              {sym} <ArrowSquareOut size={12} weight="bold" />
            </a>
            <span
              className={
                "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                scoreColor(item.score)
              }
              data-testid={`cup-score-${sym}`}
              title={`Cup quality score: ${item.score}/10`}
            >
              {item.score}/10
            </span>
            <span
              className={
                "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm flex items-center gap-1 " +
                theme.chipClass
              }
              data-testid={`cup-kind-${sym}`}
              title={kindInfo.desc}
            >
              <KindIcon size={9} weight="fill" />
              {kindInfo.label}
            </span>
            {item.fundamental_rating && (
              <span
                className={
                  "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                  (ratingColor[item.fundamental_rating] || "text-muted-fg border-subtle")
                }
                data-testid={`cup-fundamental-${sym}`}
                title={`Fundamental: ${item.fundamental_rating}${item.fundamental_score ? " (" + item.fundamental_score + "/10)" : ""}`}
              >
                <Brain size={9} weight="fill" className="inline mr-0.5" />
                {item.fundamental_rating}
              </span>
            )}
          </div>
          <div className="text-sm text-muted-fg truncate">
            {item.company_name || sym}
          </div>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          {onWatch && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onWatch({ symbol: item.symbol });
              }}
              data-testid={`cup-watch-${sym}`}
              title={watched ? "Remove from watchlist" : "Add to watchlist"}
              className={
                "p-1.5 rounded-sm border transition " +
                (watched
                  ? "bg-pos-soft border-pos text-pos hover:bg-pos-soft"
                  : "border-subtle text-muted-fg hover:text-pos hover:border-pos/40")
              }
            >
              <Star size={13} weight={watched ? "fill" : "regular"} />
            </button>
          )}
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
              Entry
            </div>
            <div className="font-mono text-lg text-primary-fg tabular-nums">
              ₹{fmt(item.entry)}
            </div>
            <div
              className={
                "text-[10px] font-mono tabular-nums " +
                (Math.abs(distFromEntry) < 1
                  ? "text-pos"
                  : distFromEntry > 0
                  ? "text-mod"
                  : "text-neg")
              }
            >
              Current ₹{fmt(item.last_price)}
              {" · "}
              {distFromEntry >= 0 ? "+" : ""}
              {fmt(distFromEntry)}%
            </div>
            {typeof item.pct_change_today === "number" && (
              <div
                className={
                  "text-[10px] font-mono tabular-nums " +
                  (item.pct_change_today >= 0 ? "text-pos" : "text-neg")
                }
                title={
                  item.last_price_refreshed_at
                    ? `Today's change · refreshed ${new Date(item.last_price_refreshed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                    : "Today's change vs prev close"
                }
              >
                {item.pct_change_today >= 0 ? "+" : ""}
                {item.pct_change_today.toFixed(2)}% today
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cup geometry strip */}
      <div className="flex items-center justify-between mb-3 px-3 py-2 bg-app border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        <div className="flex items-center gap-1.5" title="Cup depth">
          <Coffee size={10} weight="bold" className="text-pos" />
          <span className="text-secondary-fg tabular-nums">{fmt(item.cup_depth_pct, 1)}%</span> depth
        </div>
        <div className="flex items-center gap-1.5" title="Cup duration">
          <CalendarBlank size={10} weight="bold" />
          <span className="text-secondary-fg tabular-nums">{item.cup_duration_days}d</span> cup
        </div>
        <div className="flex items-center gap-1.5" title="Handle duration">
          <ChartLineUp size={10} weight="bold" />
          <span className="text-secondary-fg tabular-nums">{item.handle_duration_days}d</span> handle
        </div>
        <div className="flex items-center gap-1.5" title="Volume on breakout">
          <TrendUp size={10} weight="bold" className="text-pos" />
          <span className="text-secondary-fg tabular-nums">{fmt(item.volume_multiple, 1)}×</span> vol
        </div>
      </div>

      {/* Deep links */}
      <div className="flex items-center gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
        <a
          href={tradingViewStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`cup-tv-btn-${sym}`}
        >
          TV ↗
        </a>
        <a
          href={screenerStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`cup-scr-${sym}`}
        >
          SCR ↗
        </a>
      </div>

      {/* Trade plan */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          Trade plan
          {item.risk_reward && (
            <span className="text-secondary-fg ml-1.5">
              · R:R 1:{fmt(item.risk_reward, 1)}
            </span>
          )}
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
          Breakout above ₹{fmt(item.right_rim)}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
            <TrendUp size={9} weight="bold" /> Entry
          </div>
          <div className="text-primary-fg font-mono text-sm tabular-nums">
            ₹{fmt(item.entry)}
          </div>
        </div>
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-neg mb-1">
            <ShieldCheck size={9} weight="bold" /> SL
          </div>
          <div className="text-neg font-mono text-sm tabular-nums">
            ₹{fmt(item.stop_loss)}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-muted-fg mt-0.5">
            {item.sl_anchor === "weekly_low"
              ? "Weekly low"
              : item.sl_anchor === "monthly_low"
              ? "Monthly low"
              : "7% fallback"}
          </div>
        </div>
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
            <Target size={9} weight="bold" /> Exit
          </div>
          <div className="text-pos font-mono text-sm tabular-nums">
            ₹{fmt(item.target)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-fg flex-wrap gap-2">
        <span>
          Left rim: <span className="text-secondary-fg">{item.left_rim_date || "—"}</span>
          {" "}@<span className="text-secondary-fg"> ₹{fmt(item.left_rim)}</span>
        </span>
        <span>
          Cup low: <span className="text-secondary-fg">{item.cup_low_date || "—"}</span>
          {" "}@<span className="text-secondary-fg"> ₹{fmt(item.cup_low)}</span>
        </span>
        <span>
          Breakout:{" "}
          <span className={(item.days_since_breakout ?? 999) <= 5 ? "text-pos" : "text-secondary-fg"}>
            {item.breakout_date || "—"}
          </span>
          {item.days_since_breakout != null && (
            <span
              className={
                "ml-1 px-1 rounded-sm border " +
                ((item.days_since_breakout ?? 999) <= 5
                  ? "border-pos/40 text-pos bg-pos-soft"
                  : "border-subtle text-muted-fg")
              }
              data-testid={`cup-age-${sym}`}
            >
              {item.days_since_breakout === 0
                ? "Today"
                : `${item.days_since_breakout}d ago`}
            </span>
          )}
        </span>
      </div>

      {/* Admin-only Edit Trade Plan panel */}
      {isAdmin && item.breakout_date && (
        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
          <AdminEditTradePlan
            signal={{
              entry: item.entry,
              target: item.target,
              stop_loss: item.stop_loss,
              symbol: item.symbol,
              manually_edited: item.manually_edited,
              manually_edited_by: item.manually_edited_by,
              manually_edited_at: item.manually_edited_at,
            }}
            endpoint="/admin/cup-signals/trade-plan"
            identifier={{
              symbol: item.symbol,
              kind: item.kind,
              breakout_date: item.breakout_date,
            }}
            testIdSuffix={`cup-${sym}`}
            onSaved={(updated) => onItemUpdated?.(updated)}
          />
        </div>
      )}

      {/* Fundamental analysis (collapsible) */}
      {item.fundamental_summary ? (
        <div className="mt-4 pt-3 border-t border-subtle">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            data-testid={`cup-fund-toggle-${sym}`}
            className="flex items-center justify-between w-full text-left text-xs hover:text-primary-fg"
          >
            <span className="font-mono uppercase tracking-widest text-[10px] text-secondary-fg flex items-center gap-1">
              <Brain size={11} weight="bold" className="text-pos" /> Fundamentals (AI)
            </span>
            {open ? <CaretUp size={11} /> : <CaretDown size={11} />}
          </button>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.2 }}
              className="mt-2 space-y-2"
            >
              <p className="text-xs text-secondary-fg leading-relaxed">
                {item.fundamental_summary}
              </p>
              {item.fundamental_bullish?.length > 0 && (
                <ul className="space-y-1">
                  {item.fundamental_bullish.slice(0, 3).map((b, i) => (
                    <li
                      key={`bull-${i}-${b.slice(0, 24)}`}
                      className="text-[11px] text-secondary-fg flex gap-1.5"
                    >
                      <span className="text-pos shrink-0">+</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {item.fundamental_bearish?.length > 0 && (
                <ul className="space-y-1">
                  {item.fundamental_bearish.slice(0, 2).map((b, i) => (
                    <li
                      key={`bear-${i}-${b.slice(0, 24)}`}
                      className="text-[11px] text-secondary-fg flex gap-1.5"
                    >
                      <span className="text-neg shrink-0">−</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-subtle">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            <Warning size={11} className="text-mod" /> Fundamentals analysis pending
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function CupHandleView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 'fresh' (≤5 days since breakout), 'short', 'long'
  const [filter, setFilter] = useState("fresh");

  useEffect(() => {
    setLoading(true);
    api
      .get("/cuphandle")
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("/cuphandle load", e);
        setData({ items: [], is_premium_view: true });
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center text-muted-fg py-16" data-testid="cup-loading">
        Loading cup / flag / triangle setups…
      </div>
    );
  }

  const allItems = data?.items || [];
  // Filtering rules:
  //  - 'fresh' → only setups whose breakout happened in the last 5 trading days
  //  - 'short' / 'long' → all kinds, regardless of freshness
  const items =
    filter === "fresh"
      ? allItems.filter((it) => (it.days_since_breakout ?? 999) <= 5)
      : filter === "all"
        ? allItems
        : allItems.filter((it) => it.kind === filter);
  const isPremium = data?.is_premium_view;
  const freshCount = allItems.filter((it) => (it.days_since_breakout ?? 999) <= 5).length;
  const shortCount = allItems.filter((it) => it.kind === "short").length;
  const longCount = allItems.filter((it) => it.kind === "long").length;
  const flagCount = allItems.filter((it) => it.kind === "flag").length;
  const triCount = allItems.filter((it) => it.kind === "asc_triangle").length;

  return (
    <div data-testid="cup-view">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Coffee size={14} weight="bold" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Cup &amp; Handle / Flag / Ascending Triangle
            </div>
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-pos/40 text-pos bg-pos-soft rounded-sm">
              Volume confirmed
            </span>
          </div>
          <p className="text-xs text-muted-fg max-w-2xl leading-relaxed">
            Three high-conviction continuation patterns, all with volume-confirmed
            breakouts. <b>Cup &amp; Handle</b>: multi-month U-shape + shallow handle.
            <b> Bull Flag</b>: sharp flagpole + tight consolidation.
            <b> Ascending Triangle</b>: flat resistance + rising lows.
            Each pick auto-tagged with its pattern type.
          </p>
        </div>
        <div
          className="inline-flex border border-subtle rounded-sm p-0.5 bg-app shrink-0 flex-wrap"
          data-testid="cup-filter"
        >
          {[
            { k: "fresh", label: `Fresh · ${freshCount}`, title: "Breakouts within last 5 trading days" },
            { k: "all", label: `All · ${allItems.length}`, title: "All patterns" },
            { k: "short", label: `Cup S · ${shortCount}`, title: "Short cups (weekly-confirmed)", activeClass: "bg-mod-soft text-mod" },
            { k: "long", label: `Cup L · ${longCount}`, title: "Long cups (monthly-confirmed)", activeClass: "bg-pos-soft text-pos" },
            { k: "flag", label: `Flag · ${flagCount}`, title: "Bull flag breakouts", activeClass: "bg-pos-soft text-pos" },
            { k: "asc_triangle", label: `Tri · ${triCount}`, title: "Ascending triangle breakouts", activeClass: "bg-mod-soft text-mod" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFilter(f.k)}
              data-testid={`cup-filter-${f.k}`}
              title={f.title}
              className={
                "px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm transition " +
                (filter === f.k
                  ? (f.activeClass || "bg-pos-soft text-pos")
                  : "text-muted-fg hover:text-primary-fg")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div
          className="p-10 border border-subtle rounded-sm bg-surface text-center"
          data-testid="cup-empty"
        >
          <Coffee size={24} className="mx-auto text-muted-fg mb-3" />
          <div className="text-primary-fg font-medium mb-1">
            {filter === "fresh"
              ? "No fresh cup breakouts in the last 5 trading days"
              : `No ${filter} cup-and-handle setups right now`}
          </div>
          <div className="text-sm text-muted-fg max-w-md mx-auto">
            {filter === "fresh" ? (
              <>
                {allItems.length > 0 ? (
                  <>
                    Try the <b>Short</b> or <b>Long</b> tabs to see older
                    breakouts that are still in their valid trade window.
                  </>
                ) : (
                  <>
                    We need a textbook U-shape cup with depth 12–50%, a shallow
                    handle and a breakout above the right rim — confirmed by
                    volume AND weekly/monthly close. The detector refreshes
                    daily at 16:30 IST.
                  </>
                )}
              </>
            ) : (
              <>
                We need a textbook U-shape cup with depth 12–50%, a shallow
                handle and a breakout above the right rim — confirmed by volume
                AND weekly/monthly close. The detector refreshes daily at
                16:30 IST.
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <StockList
            items={items}
            onOpen={(it) => onOpenStock?.({ ...it, cup_mode: true })}
            onWatch={onWatch}
            watchedSet={watchedSet}
            testIdPrefix="cup-row"
            testid="cup-list"
            rowProps={(it) => {
              const info = KIND_LABEL[it.kind] || KIND_LABEL.short;
              const theme = PATTERN_THEME[it.kind] || PATTERN_THEME.short;
              return {
                iconKind:
                  it.kind === "flag"
                    ? "flag"
                    : it.kind === "asc_triangle"
                    ? "asc_triangle"
                    : it.kind === "long"
                    ? "cup_long"
                    : "cup_short",
                typeBadge: {
                  label: info.label,
                  tone:
                    theme.chipClass.includes("pos") ? "pos" :
                    theme.chipClass.includes("mod") ? "mod" : "muted",
                },
                rightHint:
                  (it.days_since_breakout ?? 999) <= 5
                    ? it.days_since_breakout === 0
                      ? "TODAY"
                      : `${it.days_since_breakout}D AGO`
                    : undefined,
              };
            }}
          />
          {!isPremium && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
              data-testid="cup-upgrade"
            >
              <div className="flex items-center gap-3">
                <Lock size={20} className="text-pos shrink-0" />
                <div>
                  <div className="font-medium text-primary-fg">
                    Showing 1 of all cup-and-handle setups
                  </div>
                  <div className="text-sm text-secondary-fg">
                    Unlock all current cup breakouts with a ₹5 trial.
                  </div>
                </div>
              </div>
              <Link
                to="/pricing"
                className="px-5 py-2.5 rounded-sm text-sm font-medium"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                Start ₹5 trial →
              </Link>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
